import React, { useState, useEffect, useRef } from "react";
import { ScrollPanel } from "primereact/scrollpanel";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Comment, CreateCommentPayload } from "../api/models/comments.models";
import { Message } from "../api/models/message.models";
import { useAuth } from "../contexts/authContext";

const MessagesContainer = styled(ScrollPanel)<{ $maxHeight?: string }>`
  flex: 1;
  padding: 1rem 2rem;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: ${(props) => props.$maxHeight || "400px"};

  .p-scrollpanel-content {
    padding-right: 1rem;
  }
`;

const MessageItem = styled.div<{ $isOwn?: boolean; $isReply?: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 0 1rem;
  align-items: ${(props) => (props.$isReply ? "flex-end" : "flex-start")};
`;

const MessageBubble = styled.div<{ $isOwn?: boolean; $isReply?: boolean }>`
  background-color: ${(props) =>
    props.$isOwn ? "var(--sumi-blue)" : "#f8f9fa"};
  color: ${(props) => (props.$isOwn ? "white" : "#495057")};
  padding: 0.75rem 1rem;
  border-radius: 1rem;
  max-width: 70%;
  word-wrap: break-word;
  margin-left: ${(props) => (props.$isReply ? "2rem" : "0")};
  border: ${(props) => (props.$isOwn ? "none" : "1px solid #dee2e6")};
  position: relative;
  margin-top: 2rem;

  &::before {
    content: "";
    position: absolute;
    ${(props) =>
      props.$isOwn
        ? `
      right: -8px;
      border-left: 8px solid #007ad9;
      border-top: 8px solid transparent;
    `
        : `
      left: -8px;
      border-right: 8px solid #f8f9fa;
      border-top: 8px solid transparent;
    `}
    top: 50%;
    transform: translateY(-50%);
  }
`;

const QuotedMessage = styled.div`
  background-color: #f1f3f4;
  border-left: 3px solid #007ad9;
  padding: 0.25rem;
  margin-bottom: 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  color: #6c757d;
  font-style: italic;
  max-height: 3em;
  overflow: hidden;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 1em;
    background: linear-gradient(transparent, #f1f3f4);
    pointer-events: none;
  }
`;

const MessageInfo = styled.div<{ $isOwn?: boolean }>`
  font-size: 0.75rem;
  color: #6c757d;
  margin: 0.25rem 0.5rem 0 0.5rem;
  text-align: ${(props) => (props.$isOwn ? "right" : "left")};
`;

const ReplyButton = styled.button`
  background: none;
  border: none;
  color: #007ad9;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  margin-top: 0.25rem;

  &:hover {
    text-decoration: underline;
  }
`;

const InputContainer = styled.div`
  padding: 1rem 2rem;
  border-top: 1px solid #e1e5e9;
  background-color: #f8f9fa;
`;

const ReplyIndicator = styled.div`
  background-color: #e3f2fd;
  border-left: 4px solid #007ad9;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: #495057;
  border-radius: 0 0.25rem 0.25rem 0;
`;

const InputActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  gap: 0.5rem;
`;

const CancelReplyButton = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    color: #495057;
    text-decoration: underline;
  }
`;

const CommentsContainer = styled.div<{ $isDialogMode?: boolean }>`
  display: flex;
  flex-direction: column;
  ${(props) => props.$isDialogMode && "height: fit-content;"}
  overflow: hidden;
`;

const AccordionCommentsContainer = styled.div`
  margin: 2rem 0;

  .p-accordion .p-accordion-content {
    padding: 0;
  }

  .p-accordion-header .p-accordion-header-text {
    font-weight: 500;
  }
`;

interface NormalizedComment {
  id: number;
  sender: string;
  body: string;
  reportId?: number;
  replyId?: number;
  subject?: string;
  replies?: (NormalizedComment & { originalMessage?: NormalizedComment })[];
  originalMessage?: NormalizedComment;
}

interface CommentsSectionProps {
  // Either provide comments (from useComments) or messages (from reportData.messages)
  comments?: Comment[];
  messages?: Message[];
  reportId: number;
  isLoading?: boolean;
  // For adding new comments
  onAddComment?: (payload: CreateCommentPayload) => void;
  isAddingComment?: boolean;
  // Display mode
  mode?: "dialog" | "accordion";
  // Custom height for scroll panel
  maxHeight?: string;
  // Accordion props
  accordionHeader?: string;
  initiallyExpanded?: boolean;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  comments,
  reportId,
  isLoading = false,
  onAddComment,
  isAddingComment = false,
  mode = "dialog",
  maxHeight,
  accordionHeader,
  initiallyExpanded = false,
}) => {
  const { t } = useTranslation();
  const { userName } = useAuth();
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<NormalizedComment | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | undefined>(
    initiallyExpanded ? 0 : undefined,
  );

  // Normalize data structure - convert messages to comment-like structure
  const normalizeData = (comments?: Comment[]): NormalizedComment[] => {
    if (comments) {
      return comments.map((comment) => ({
        id: comment.id,
        sender: comment.sender,
        body: comment.body,
        reportId: comment.reportId,
        replyId: comment.replyId,
      }));
    }

    return [];
  };

  const normalizedData = normalizeData(comments);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && mode === "dialog") {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [normalizedData, mode]);

  // Reset form when component unmounts or when not expanded in accordion mode
  useEffect(() => {
    if (mode === "accordion" && activeIndex === undefined) {
      setNewComment("");
      setReplyingTo(null);
    }
  }, [activeIndex, mode]);

  const handleSendComment = () => {
    if (!newComment.trim() || !onAddComment) return;

    const payload: CreateCommentPayload = {
      body: newComment.trim(),
      reportId: reportId,
      replyId: replyingTo?.id,
    };

    onAddComment(payload);
    setNewComment("");
    setReplyingTo(null);
  };

  const handleReply = (comment: NormalizedComment) => {
    if (comments) {
      // Only allow replies for actual comments (not messages)
      setReplyingTo(comment);
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const organizeComments = (data: NormalizedComment[]) => {
    const topLevelComments = data.filter((item) => !item.replyId);
    const replies = data.filter((item) => item.replyId);

    return topLevelComments.map((comment) => ({
      ...comment,
      replies: replies
        .filter((reply) => reply.replyId === comment.id)
        .map((reply) => ({
          ...reply,
          originalMessage: comment,
        })),
    }));
  };

  const organizedData = organizeComments(normalizedData);

  const renderMessage = (
    item: NormalizedComment & {
      replies?: (NormalizedComment & { originalMessage?: NormalizedComment })[];
      originalMessage?: NormalizedComment;
    },
    isReply: boolean = false,
  ) => {
    const isOwnMessage = item.sender === userName;
    // Allow replies when we have comments (regardless of messages), disable for message-only mode
    const allowReplies = !!comments;

    return (
      <React.Fragment key={item.id}>
        <MessageItem $isOwn={isOwnMessage} $isReply={isReply}>
          <MessageBubble $isOwn={isOwnMessage} $isReply={isReply}>
            {/* Show quoted message for replies */}
            {isReply && item.originalMessage && (
              <QuotedMessage>
                "
                {item.originalMessage.body.length > 100
                  ? `${item.originalMessage.body.substring(0, 100)}...`
                  : item.originalMessage.body}
                "
              </QuotedMessage>
            )}
            {item.subject && (
              <div style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
                {item.subject}
              </div>
            )}
            {item.body}
          </MessageBubble>
          <MessageInfo $isOwn={isOwnMessage}>
            {t("label-sender")}: {item.sender}
            {!isReply && allowReplies && onAddComment && (
              <ReplyButton onClick={() => handleReply(item)}>
                {t("cmd-reply")}
              </ReplyButton>
            )}
          </MessageInfo>
        </MessageItem>

        {/* Render replies */}
        {item.replies?.map((reply) => renderMessage(reply, true))}
      </React.Fragment>
    );
  };

  const renderCommentsContent = () => {
    if (isLoading) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "#6c757d",
          }}
        >
          {t("label-loading")}...
        </div>
      );
    }

    return (
      <CommentsContainer $isDialogMode={mode === "dialog"}>
        <MessagesContainer $maxHeight={maxHeight}>
          {organizedData.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: "#6c757d",
              }}
            >
              {t("header-no-comments")}
            </div>
          ) : (
            organizedData.map((item) => renderMessage(item))
          )}
          <div ref={messagesEndRef} />
        </MessagesContainer>

        {onAddComment && (
          <InputContainer>
            {replyingTo && (
              <ReplyIndicator>
                {t("label-replying-to")}: {replyingTo.body.substring(0, 50)}
                {replyingTo.body.length > 50 ? "..." : ""}
              </ReplyIndicator>
            )}

            <InputTextarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t("label-message-placeholder")}
              rows={3}
              style={{ width: "100%", resize: "vertical" }}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendComment();
                }
              }}
            />

            <InputActions>
              <div>
                {replyingTo && (
                  <CancelReplyButton onClick={cancelReply}>
                    {t("cmd-cancel")}
                  </CancelReplyButton>
                )}
              </div>
              <Button
                label={replyingTo ? t("cmd-send-reply") : t("cmd-send")}
                onClick={handleSendComment}
                disabled={!newComment.trim() || isAddingComment}
                loading={isAddingComment}
                size="small"
              />
            </InputActions>
          </InputContainer>
        )}
      </CommentsContainer>
    );
  };

  if (mode === "accordion") {
    return (
      <AccordionCommentsContainer>
        <Accordion
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index as number)}
        >
          <AccordionTab
            header={
              accordionHeader ||
              `${t("header-internal-comments")} (${organizedData.length})`
            }
          >
            {renderCommentsContent()}
          </AccordionTab>
        </Accordion>
      </AccordionCommentsContainer>
    );
  }

  return renderCommentsContent();
};

export default CommentsSection;
